import * as React from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { Box, TextInput, Group, Button, NumberInput, Switch } from '@mantine/core'
import { isNotEmpty, useForm } from "@mantine/form";
import axios from 'axios';
import type { IResponse } from "../../types/api";
import { showMessage } from "../../lib/notification";
import { CLIENT_ROUTE, API_ROUTE, CLIENT_TITLE } from "./constants";
import produce from "immer";
import { DatePicker } from "@mantine/dates";
import dayjs from "dayjs";
import type { IBanner } from "./types";
import { FileWithPath, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import FileUpload from "../../components/file-upload";
import Page from "../../components/layout/Page";
import { assignFiles } from "../../utils/form";
import { useFormStyles } from "../../styles/form";

interface FormValues extends Pick<IBanner, 'title' | 'link' | 'order' | 'isActive'> {
    startDate: Date | null | string;
    endDate: Date | null | string;
    image: string | FileWithPath | null;
    mobileImage: string | FileWithPath | null;
}

interface IParams extends Omit<FormValues, 'image' | 'mobileImage'> {
    image?: string;
    mobileImage?: string;
}

const EditBanner = () => {

    const { state: id = null } = useLocation();

    const { classes } = useFormStyles();
    const navigate = useNavigate();

    const form = useForm<FormValues>({
        initialValues: {
            startDate: new Date(),
            endDate: new Date(),
            link: '',
            order: 0,
            title: '',
            image: null,
            isActive: false,
            mobileImage: null,
        },

        validate: {
            title: isNotEmpty('Нэр оруулна уу'),
            image: isNotEmpty('Зураг оруулна уу'),
            mobileImage: isNotEmpty('Утасны зураг оруулна уу'),
            startDate: isNotEmpty('Эхлэх огноо оруулна уу'),
            endDate: isNotEmpty('Дуусах огноо оруулна уу'),
        },
    });

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.get<IResponse<IBanner>>(`${API_ROUTE}/${id}`);
            const values = produce(data.result, draft => {
                const startDate = dayjs(draft.startDate);
                if (startDate.isValid()) {
                    draft.startDate = startDate.toDate();
                }
                const endDate = dayjs(draft.endDate);
                if (endDate.isValid()) {
                    draft.endDate = endDate.toDate();
                }
            })
            form.setValues(values)
            form.resetDirty();
        }
        void fetchData();
    }, []);

    const handleSubmit = async (values: typeof form.values) => {
        const files = await assignFiles({
            image: values.image,
            mobileImage: values.mobileImage
        }, 'banner')
        const params: IParams = {
            ...values,
            ...files
        }
        const { data } = await axios.post<IResponse<any>>(`${API_ROUTE}/${id}`, params);
        if (data.statusCode === 200) {
            navigate(CLIENT_ROUTE);
            showMessage('Амжилттай', 'green')
        } else {
            showMessage('Алдаа гарлаа')
        }
    }

    return (
        <Page>
            <Page.Header title={`${CLIENT_TITLE} засах`} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Box sx={{ maxWidth: 500 }} mx="auto">

                    <form onSubmit={form.onSubmit(handleSubmit)} className={classes.form}>
                        <TextInput
                            withAsterisk
                            label="Нэр"
                            placeholder="Нэр оруулна уу"
                            {...form.getInputProps('title')}
                        />

                        <FileUpload
                            value={form.values.image}
                            onChange={(value) => {
                                form.setFieldValue('image', value);
                            }}
                            label='Таблетны зураг (1:1.3)'
                            accept={IMAGE_MIME_TYPE}
                            ratio={'1/1.3'}
                        />

                        <FileUpload
                            value={form.values.mobileImage}
                            onChange={(value) => {
                                form.setFieldValue('mobileImage', value);
                            }}
                            label='Таблетны зураг (2:1)'
                            accept={IMAGE_MIME_TYPE}
                            ratio={'2/1'}
                        />

                        <DatePicker
                            withAsterisk
                            placeholder="Эхлэх огноо"
                            label="Эхлэх огноо"
                            {...form.getInputProps('startDate')}
                        />

                        <DatePicker
                            withAsterisk
                            placeholder="Дуусах огноо"
                            label="Дуусах огноо"
                            {...form.getInputProps('endDate')}
                        />

                        <TextInput
                            label="Холбоос"
                            placeholder="Холбоос оруулна уу"
                            {...form.getInputProps('link')}
                        />

                        <NumberInput
                            label="Дараалал"
                            placeholder="Дараалал"
                            {...form.getInputProps('order')}
                        />

                        <Switch
                            label="Идэвхтэй эсэх"
                            {...form.getInputProps('isActive', { type: 'checkbox' })}
                        />
                    </form>
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Button color='color-secondary' onClick={() => form.onSubmit(handleSubmit)()}>Хадгалах</Button>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default EditBanner;